import { GROUPS, GROUP_ORDER, INHERITED_CHILD_TITLES, NAV_CONFIG } from './config';
import { DETAIL_ROUTES, pathOf, targetForTitle } from './routes';
import { titlesForRole } from './presets';
import {
  BreadcrumbCrumb, NavCategory, NavNode, NavScreenGroup, NavSearchEntry, ProductId, ResolveNavInput,
  ResolvedNavCategory, ResolvedNavNode,
} from './types';

function productEnabled(flag: ProductId | undefined, clientProducts: ResolveNavInput['clientProducts']): boolean {
  if (!flag) { return true; }
  return clientProducts?.[flag] !== false;
}

function resolveNode(
  node: NavNode,
  input: ResolveNavInput,
  allowedTitles: Set<string>,
  gate: boolean,
): ResolvedNavNode | null {
  if (node.disabled) {
    return { ...node, label: node.label ?? node.title, target: { kind: 'internal', to: '' }, children: undefined };
  }
  if (gate) {
    if (!allowedTitles.has(node.title)) { return null; }
    if (node.capability && !input.can(node.capability)) { return null; }
    if (!productEnabled(node.productFlag, input.clientProducts)) { return null; }
  }

  // Only INHERITED_CHILD_TITLES ride on the parent; any other child is gated on its own title.
  const children = (node.children ?? [])
    .map((child) => resolveNode(child, input, allowedTitles, !INHERITED_CHILD_TITLES.includes(child.title)))
    .filter((child): child is ResolvedNavNode => child !== null);

  const target = targetForTitle(node.title, input.roleNumber);
  if (!pathOf(target) && children.length === 0) { return null; }

  return { ...node, label: node.label ?? node.title, target, children: children.length ? children : undefined };
}

// A placeholder or mirror must not conjure a product tile or a group section the role otherwise has no screens in.
function withoutOrphanPlaceholders(screens: ResolvedNavNode[]): ResolvedNavNode[] {
  const own = (s: ResolvedNavNode) => !s.disabled && !s.mirror;
  if (!screens.some(own)) { return []; }
  return screens.filter((s) => own(s) || !s.group || screens.some((o) => own(o) && o.group === s.group));
}

const MUTABLE_REFERRAL_GROUPS: readonly string[] = [GROUPS.referrals, GROUPS.reports];

function muteReferralLinks(product: ProductId, screens: ResolvedNavNode[], input: ResolveNavInput): ResolvedNavNode[] {
  if (product !== 'gateway' || !input.ctx.gatewayReferralLinksDisabled) { return screens; }
  return screens.map((s) => (s.group && MUTABLE_REFERRAL_GROUPS.includes(s.group) ? { ...s, disabled: true } : s));
}

export function resolveNav(config: NavCategory[], input: ResolveNavInput): ResolvedNavCategory[] {
  const allowedTitles = new Set(titlesForRole(input.roleNumber, input.ctx));

  return config
    .filter((category) => productEnabled(category.productFlag, input.clientProducts))
    .map((category) => ({
      product: category.product,
      title: category.title,
      productLabel: category.productLabel,
      placement: category.placement ?? 'rail',
      icon: category.icon,
      screens: muteReferralLinks(category.product, withoutOrphanPlaceholders(category.screens
        .map((node) => resolveNode(node, input, allowedTitles, true))
        .filter((node): node is ResolvedNavNode => node !== null)), input),
    }))
    .filter((category) => category.screens.length > 0);
}

interface FlatEntry {
  path: string;
  product: ProductId;
  trail: BreadcrumbCrumb[];
}

function flatten(categories: ResolvedNavCategory[]): FlatEntry[] {
  const out: FlatEntry[] = [];
  const walk = (nodes: ResolvedNavNode[], product: ProductId, categoryTitle: string, ancestors: BreadcrumbCrumb[]) => {
    for (const node of nodes) {
      const path = pathOf(node.target);
      const crumb: BreadcrumbCrumb = { title: node.label, path: path || undefined };
      const trail = [{ title: categoryTitle }, ...ancestors, crumb];
      if (path) { out.push({ path, product, trail }); }
      if (node.children) { walk(node.children, product, categoryTitle, [...ancestors, crumb]); }
    }
  };
  for (const category of categories) {
    walk(category.screens, category.product, category.title, []);
  }
  return out;
}

/** Longest-prefix match so child routes (e.g. `/manageb/report-builder/42`) still resolve. */
function bestMatch(entries: FlatEntry[], path: string): FlatEntry | undefined {
  const url = path.split('?')[0].split('#')[0];
  let best: FlatEntry | undefined;
  for (const entry of entries) {
    if (url === entry.path || url.startsWith(entry.path + '/')) {
      if (!best || entry.path.length > best.path.length) { best = entry; }
    }
  }
  return best;
}

function detailRouteFor(path: string) {
  const url = path.split('?')[0].split('#')[0];
  return DETAIL_ROUTES.find((route) => url === route.prefix || url.startsWith(route.prefix + '/'));
}

/** Detail pages resolve through their parent screen, so both product and trail come from it. */
function matchWithDetails(entries: FlatEntry[], path: string): { entry: FlatEntry; detailTitle?: string } | undefined {
  const direct = bestMatch(entries, path);
  if (direct) { return { entry: direct }; }
  const detail = detailRouteFor(path);
  if (!detail) { return undefined; }
  const parent = bestMatch(entries, detail.parentPath);
  return parent ? { entry: parent, detailTitle: detail.title } : undefined;
}

export function productForPath(categories: ResolvedNavCategory[], path: string): ProductId | undefined {
  return matchWithDetails(flatten(categories), path)?.entry.product;
}

// Drops crumbs that navigate nowhere (pathless category labels); the leaf always survives.
export function dropPathlessCrumbs(trail: BreadcrumbCrumb[]): BreadcrumbCrumb[] {
  return trail.filter((c, i) => c.path || i === trail.length - 1);
}

export function breadcrumbTrailForPath(categories: ResolvedNavCategory[], path: string): BreadcrumbCrumb[] {
  const match = matchWithDetails(flatten(categories), path);
  if (!match) { return []; }
  const trail = match.detailTitle ? [...match.entry.trail, { title: match.detailTitle }] : match.entry.trail;
  return dropPathlessCrumbs(trail);
}

export function railCategories(categories: ResolvedNavCategory[]): ResolvedNavCategory[] {
  return categories.filter((category) => category.placement === 'rail');
}

/** Screens flagged `rail`, in config order: rendered as links above the product tiles. */
export function railScreens(categories: ResolvedNavCategory[]): ResolvedNavNode[] {
  return ([] as ResolvedNavNode[]).concat(...categories.map((c) => c.screens.filter((s) => s.rail)));
}

/** Account/administration screens reached from the rail footer, flattened across categories. */
export function settingsScreens(categories: ResolvedNavCategory[]): ResolvedNavNode[] {
  return ([] as ResolvedNavNode[]).concat(
    ...categories.filter((c) => c.placement === 'settings').map((c) => c.screens),
  );
}

/**
 * Panel sections in GROUP_ORDER. Ungrouped screens come first so a product with no grouping
 * (Comply) renders exactly as it did before sections existed.
 */
export function groupScreens(allScreens: ResolvedNavNode[]): NavScreenGroup[] {
  const screens = allScreens.filter((screen) => !screen.rail);
  const ungrouped = screens.filter((screen) => !screen.group);
  const groups: NavScreenGroup[] = ungrouped.length ? [{ screens: ungrouped }] : [];

  const seen = new Set<string>();
  const ordered = [
    ...GROUP_ORDER.filter((title) => screens.some((s) => s.group === title)),
    // A group added to config without a GROUP_ORDER entry still renders, after the known ones.
    ...screens.map((s) => s.group).filter((title): title is string => !!title && !GROUP_ORDER.includes(title)),
  ];

  for (const title of ordered) {
    if (seen.has(title)) { continue; }
    seen.add(title);
    groups.push({ title, screens: screens.filter((s) => s.group === title) });
  }
  return groups;
}

/** Flat searchable list for the command palette: every screen and child the role can reach. */
export function navSearchEntries(categories: ResolvedNavCategory[]): NavSearchEntry[] {
  const out: NavSearchEntry[] = [];
  const walk = (nodes: ResolvedNavNode[], category: ResolvedNavCategory, ancestors: string[]) => {
    for (const node of nodes) {
      const path = pathOf(node.target);
      const context = [category.title, ...ancestors, node.group].filter(Boolean).join(' › ');
      if (path && !node.disabled) {
        out.push({
          title: node.label,
          path,
          product: category.product,
          context,
          state: node.target.state,
        });
      }
      if (node.children) { walk(node.children, category, [...ancestors, node.label]); }
    }
  };
  for (const category of categories) { walk(category.screens, category, []); }
  return out;
}

export const DEFAULT_NAV_CONFIG = NAV_CONFIG;
